<script setup lang="ts">
import lodashMax from 'lodash-es/max';
import lodashMin from 'lodash-es/min';

import { useApiGetPowertrainWarrantyDurations } from '~/apiClient';
import type { VasPowertrainWarrantyDuration } from '~/components/Vas/VasPowertrainWarrantyFormField.vue';
import { ROUTE_NAMES } from '~/constants/routes';

// EVENTS
const emit = defineEmits<{
    selectPowertrainWarranty: [number | undefined, VasPowertrainWarrantyDuration | undefined];
}>();

// MODEL
const isModalOpen = defineModel<boolean>('open', { required: true });

// COMPOSABLES
const { isMobile } = useResponsive();
const { tt, tn } = useTypedI18n();

// DATA
const { data } = useApiGetPowertrainWarrantyDurations();
const price = ref<number>();
const duration = ref<VasPowertrainWarrantyDuration>();

const durationMonths = computed(() => data.value?.durations.map(d => d.durationMonths));
const durationFrom = computed(() => lodashMin(durationMonths.value));
const durationTo = computed(() => lodashMax(durationMonths.value));
const maxRefound = 25_000;

// HANDLERS
const onConfirm = (): void => {
    emit('selectPowertrainWarranty', price.value, duration.value);
    isModalOpen.value = false;
};
</script>

<template>
    <UModal v-model:open="isModalOpen" :fullscreen="isMobile" :title="tt('sdp.vas.powertrainWarrantyModal.title')" v-bind="modalNoDescAriaFixupProps">
        <template #body>
            <div class="space-y-4">
                <i18n-t scope="global" tag="p" keypath="sdp.vas.powertrainWarrantyModal.description1.base" class="text-muted">
                    <template #bold1>
                        <span class="font-bold"> {{ tt('sdp.vas.powertrainWarrantyModal.description1.bold1') }} </span>
                    </template>
                    <template #bold2>
                        <span class="font-bold"> {{ tt('sdp.vas.powertrainWarrantyModal.description1.bold2') }} </span>
                    </template>
                    <template #highlight>
                        <span class="text-primary font-bold"> {{ tt('sdp.vas.powertrainWarrantyModal.description1.highlight') }} </span>
                    </template>
                </i18n-t>
                <i18n-t scope="global" tag="p" keypath="sdp.vas.powertrainWarrantyModal.description2.base" class="text-muted">
                    <template #bold1>
                        <span class="font-bold">
                            {{ tt('sdp.vas.powertrainWarrantyModal.description2.bold1', { from: durationFrom, to: durationTo }) }}
                        </span>
                    </template>
                    <template #bold2>
                        <span class="font-bold">
                            {{ tt('sdp.vas.powertrainWarrantyModal.description2.bold2', { refound: tn(maxRefound, 'currency') }) }}
                        </span>
                    </template>
                </i18n-t>

                <div class="space-y-1">
                    <VasPowertrainWarrantyFormField v-model:duration="duration" v-model:price="price" />
                    <p class="text-muted text-sm">
                        <i18n-t scope="global" tag="p" keypath="sdp.vas.powertrainWarrantyModal.termsAndConditions.base" class="text-muted">
                            <template #link>
                                <NuxtLinkLocale class="text-primary underline" :to="ROUTE_NAMES.TERMS_AND_CONDITIONS" target="_blank">
                                    {{ tt('sdp.vas.powertrainWarrantyModal.termsAndConditions.link') }}
                                </NuxtLinkLocale>
                            </template>
                        </i18n-t>
                    </p>
                </div>
                <UButton size="xl" :label="tt('sdp.vas.powertrainWarrantyModal.confirm')" block @click="onConfirm" />
            </div>
        </template>
    </UModal>
</template>
